import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';
import { inject } from 'vue';
import { onMounted } from 'vue';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { watch } from 'vue';

import { ApplicationOption } from '@/shared/model/application-option';
//--- Function imports

//### Service imports
import AccountService from '@/account/account.service';
//--- Service imports

//### Model imports
import { Authority } from '@/shared/model/authority';
//--- Model imports

//### Component imports
import { SidebarMenu } from 'vue-sidebar-menu';
//--- Component imports

//### External Properties
//--- External Properties

//### Setup

export default /*#__PURE__*/_defineComponent({
  __name: 'sidebar',
  setup(__props, { expose: __expose }) {
  __expose();

//### Function imports
const accountService = inject<AccountService>('accountService');

const i18n = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
//--- Setup

//### Store/Reactive Data
const currentApplication = computed(() => store.getters.currentApplication);
const isPhoneDevice = computed(() => store.getters.isPhoneDevice);
const menuExpanded = computed(() => store.getters.menuExpanded);

watch(route, () => {
	store.commit('setCurrentApplication', route.meta.applicationOption);
	getRouteAndCreateMenu();
});

watch(menuExpanded, () => {
	setToolTipPosition();
});
//--- Store/Reactive Data

//### Variables const let
const applicationOptions = ref([
	{
		defaultPath: 'journal',
		icon: 'clock',
		label: 'ZEF',
		value: ApplicationOption.ZEF,
	},
	{
		defaultPath: 'settings/program',
		icon: 'gear',
		label: 'Einstellungen',
		value: ApplicationOption.SETTINGS,
	},
]);
const tooltipPos = ref({ position: menuExpanded.value ? 'top' : 'right' });

const visiblePhone = ref(false);
//--- Variables const let

//### Component Lifecycle
onMounted(() => {
	if (accountService.hasAnyRole([Authority.SUPERADMIN, Authority.PZE])) {
		applicationOptions.value.splice(1, 0, {
			defaultPath: 'time-line',
			label: 'PZE',
			value: ApplicationOption.PZE,
			icon: 'table',
		});
	}
});
//--- Component Lifecycle

//### Functions
function createZefMenu(menu: Array<any>): Array<any> {
	if (currentApplication.value === ApplicationOption.ZEF) {
		menu.push({
			title: i18n.t('menu.dashboard.dashboard'),
			href: '/dashboard-zef',
			icon: 'fa-solid fa-chart-line',
		});

		if (accountService.hasRole(Authority.ADMIN)) {
			menu.push({
				title: i18n.t('menu.baseData'),
				icon: 'fa fa-database',
				child: [
					{
						title: i18n.t('menu.absenceType'),
						href: '/absence-type',
						icon: 'fa fa-user-clock',
					},
					{
						title: i18n.t('menu.serviceAgreements'),
						href: '/service-agreement',
						icon: 'fa fa-handshake',
					},
					{
						title: i18n.t('menu.documentType'),
						href: '/document-type',
						icon: 'fa fa-solid fa-file',
					},
				],
			});
		}

		if (accountService.hasAnyRole([Authority.ADMIN, Authority.ADMIN_EMPLOYEE])) {
			const administrationMenu = {
				title: i18n.t('menu.management'),
				icon: 'fa fa-briefcase',
				child: [],
			};

			if (accountService.hasRole(Authority.ADMIN)) {
				administrationMenu.child.push({
					title: i18n.t('menu.employee'),
					href: '/employee',
					icon: 'fa fa-users',
				});
			}

			administrationMenu.child.push({
				title: i18n.t('menu.billing'),
				href: '/billing',
				icon: 'fa fa-file-invoice',
			});

			if (accountService.hasRole(Authority.ADMIN_EMPLOYEE)) {
				administrationMenu.child.push(
					{
						title: i18n.t('menu.absence.open'),
						href: '/supervisor-requests-open',
						icon: 'fa fa-envelope-open',
					},
					{
						title: i18n.t('menu.absence.approved'),
						href: '/supervisor-requests-closed',
						icon: 'fa fa-envelope',
					},
				);
			}
			menu.push(administrationMenu);
		}

		if (accountService.hasRole(Authority.USER)) {
			menu.push({
				title: i18n.t('menu.absences'),
				icon: 'fa fa-user-clock',
				child: [
					{
						title: i18n.t('menu.absence.open'),
						href: '/requests-open',
						icon: 'fa fa-envelope-open',
					},
					{
						title: i18n.t('menu.absence.approved'),
						href: '/requests-closed',
						icon: 'fa fa-envelope',
					},
					{
						title: i18n.t('menu.absence.teamCalendar'),
						href: '/team-calendar',
						icon: 'fa fa-calendar',
					},
				],
			});

			menu.push({
				title: i18n.t('menu.journal'),
				href: '/journal',
				icon: 'fa fa-calendar',
			});
		}
	}
	return menu;
}

function createPzeMenu(menu: Array<any>): Array<any> {
	if (currentApplication.value === ApplicationOption.PZE) {
		menu.push({
			title: i18n.t('menu.dashboard.dashboard'),
			href: '/dashboard-pze',
			icon: 'fa-solid fa-chart-line',
		});

		if (accountService.hasRole(Authority.ADMIN)) {
			menu.push({
				title: i18n.t('menu.management'),
				icon: 'fa fa-briefcase',
				child: [
					{
						title: i18n.t('menu.projects'),
						href: '/admin/projects',
						icon: 'fa fa-cube',
					},
					{
						title: i18n.t('menu.projectCategories'),
						href: '/admin/project-category',
						icon: 'fa fa-cat',
					},
					{
						title: i18n.t('menu.projectCustomers'),
						href: '/admin/project-customer',
						icon: 'fa fa-user',
					},
					{
						title: i18n.t('menu.projectTemplates'),
						href: '/admin/project-templates',
						icon: 'fa fa-list',
					},
				],
			});

			menu.push({
				title: i18n.t('menu.projectControl'),
				icon: 'fa fa-eye',
				child: [
					{
						title: i18n.t('menu.overview'),
						href: '/admin/time-line-overview',
						icon: 'fa fa-eye',
					},
					{
						title: i18n.t('menu.orders'),
						href: '/admin/orders',
						icon: 'fa fa-business-time',
					},
					{
						title: i18n.t('menu.teamCalendar'),
						href: '/admin/team-calendar',
						icon: 'fa fa-calendar',
					},
				],
			});
		}

		menu.push({
			title: i18n.t('menu.calenderView'),
			href: '/view-calendar',
			icon: 'fa fa-calendar',
		});

		menu.push({
			title: i18n.t('menu.timeRecording'),
			href: '/time-line',
			icon: 'fa fa-hourglass',
		});

		menu.push({
			title: i18n.t('menu.favorites'),
			href: '/favorites',
			icon: 'fa fa-bookmark',
		});
	}
	return menu;
}

function createSettingsMenu(menu: Array<any>): Array<any> {
	if (currentApplication.value === ApplicationOption.SETTINGS) {
		if (accountService.hasRole(Authority.SUPERADMIN)) {
			menu.push({
				title: i18n.t('menu.admin.main'),
				icon: 'fa fa-toolbox',
				child: [
					{
						href: '/admin/tenant-management',
						icon: 'fa fa-users',
						title: i18n.t('menu.admin.tenantManagement'),
					},
					{
						href: '/admin/gateway',
						icon: 'fa fa-network-wired',
						title: i18n.t('menu.admin.gateway'),
					},
					{
						href: '/admin/metrics',
						icon: 'fa fa-chart-bar',
						title: i18n.t('menu.admin.metrics'),
					},
					{
						href: '/admin/health',
						icon: 'fa fa-house-chimney-medical',
						title: i18n.t('menu.admin.health'),
					},
					{
						href: '/admin/apidocs',
						icon: 'fa fa-hashtag',
						title: i18n.t('menu.admin.apidocs'),
					},
					{
						href: '/admin/configuration',
						icon: 'fa fa-gears',
						title: i18n.t('menu.admin.configuration'),
					},
				],
			});
		}

		if (accountService.hasAnyRole([Authority.SUPERADMIN, Authority.ADMIN])) {
			menu.push({
				href: '/admin/user-management',
				icon: 'fa fa-user',
				title: i18n.t('menu.admin.userManagement'),
			});
		}

		const programMenu = {
			href: '/settings/program',
			icon: 'fa fa-gear',
			title: i18n.t('menu.settingProgram'),
		};

		if (accountService.hasRole(Authority.ADMIN)) {
			menu.push({
				icon: 'fa fa-gear',
				title: i18n.t('menu.settingProgram'),
				child: [
					{
						href: '/settings/company',
						icon: 'fa fa-users',
						title: i18n.t('company.dataLabel'),
					},
					{
						...programMenu,
					},
				],
			});
		} else {
			menu.push({
				...programMenu,
			});
		}
	}

	return menu;
}

function setToolTipPosition() {
	tooltipPos.value = { position: menuExpanded.value ? 'top' : 'right' };
}

function getRouteAndCreateMenu() {
	let menu = [];

	menu = createZefMenu(menu);
	menu = createPzeMenu(menu);
	menu = createSettingsMenu(menu);

	return menu;
}

function toggleMenu() {
	store.commit('toggleMenu', !menuExpanded.value);
}

function switchApplication(appName, defaultPath): void {
	if (appName && currentApplication.value !== appName) {
		store.commit('setCurrentApplication', appName);
		router.push(`/${defaultPath}`);
	}
}

function toggleMenuPhone() {
	visiblePhone.value = !visiblePhone.value;
}

function closeMenu() {
	visiblePhone.value = false;
}
//--- Functions

const __returned__ = { accountService, i18n, route, router, store, currentApplication, isPhoneDevice, menuExpanded, applicationOptions, tooltipPos, visiblePhone, createZefMenu, createPzeMenu, createSettingsMenu, setToolTipPosition, getRouteAndCreateMenu, toggleMenu, switchApplication, toggleMenuPhone, closeMenu, get SidebarMenu() { return SidebarMenu } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})